import React, { useMemo } from 'react';

import { type DefaultRenderActivity, type IActivityRenderer, type IActivityRendererProps } from './types';

type ActivityRendererManagerProps<T extends IActivityRendererProps> = {
  defaultRenderer: DefaultRenderActivity;
  renderers: Array<IActivityRenderer<T>>;
} & T;

/**
 * `ActivityRendererManager` is a React functional component that manages the rendering of activities.
 * It uses a list of renderers to find the appropriate renderer for a given activity.
 * If no suitable renderer is found, it falls back to a default renderer.
 *
 * @template T - The type of the activity renderer props.
 * @param {ActivityRendererManagerProps<T>} props - The properties for the ActivityRendererManager component.
 * @param {IActivity} props.activity - The activity to be rendered.
 * @param {React.ComponentType<T>} props.defaultRenderer - The default renderer to use if no suitable renderer is found.
 * @param {Array<{ canRender: (activity: IActivity, isFromUser: boolean) => boolean, render: React.ComponentType<T> }>} props.renderers - The list of renderers to choose from.
 * @param {object} props.params - Additional parameters for rendering.
 * @returns {React.ReactNode} The rendered activity.
 */
export const ActivityRendererManager = React.memo(
  <T extends IActivityRendererProps>(props: ActivityRendererManagerProps<T>) => {
    const {
      activity,
      defaultRenderer,
      renderers,
      params,
    } = props;

    const isFromUser = useMemo(() => activity.from.name === params.username, [activity.from.name, params.username]);

    // find the first renderer that can render the activity
    const Renderer = renderers.find((renderer) => renderer.canRender?.(activity, isFromUser));
    if (Renderer) {
      return (
        <Renderer
          {...props}
        />
      );
    }

    // could not find a renderer, fallback to the default behavior
    return defaultRenderer(activity as any, params);
  },
) as <T extends IActivityRendererProps>(props: ActivityRendererManagerProps<T>) => React.ReactNode;
